import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { downloadCaches, useSyncStatus, type RosterFarmer } from '@/offline'
import { useToast } from '@/ui'
import { scrubSyncedBank } from '../../data/actions'
import { useQueuedBanks } from '../../data/hooks'
import { useGate } from '../../data/useGate'
import { farmerMatches } from '../../model/search'
import { FarmerRow } from '../delivery/Find'
import { Btn, Footer, Icon, V1Header, V1Screen, WaitCta } from '../kit'
import { C } from '../style'
import { tr } from '../text'
import { BankCollectScreen } from './BankCollect'

/** How many farmers show before she types or taps "show all" (as on the find screen). */
const QUICK = 30

type Bank = {
  verified: boolean
  last4: string | null
  pendingLast4: string | null
  /** Typed in with no internet: on this phone, not checked yet. */
  queuedLast4: string | null
}

function bankOf(
  f: RosterFarmer,
  added: ReadonlyMap<string, string>,
  queued: ReadonlyMap<string, string>,
): Bank {
  return {
    verified: Boolean(f.bank?.verified && f.bank.account_last4),
    last4: f.bank?.account_last4 ?? null,
    pendingLast4: added.get(f.user_id) ?? f.bank?.pending_last4 ?? null,
    queuedLast4: queued.get(f.user_id) ?? null,
  }
}

function BankNote({ b }: { b: Bank }) {
  const [text, color] = b.verified
    ? [tr('gate.v1.bank.hasVerified', { last4: b.last4! }), C.ok]
    : b.pendingLast4
      ? [tr('gate.v1.bank.hasPending', { last4: b.pendingLast4 }), C.accDark]
      : b.queuedLast4
        ? [tr('gate.v1.bank.hasQueued', { last4: b.queuedLast4 }), C.accDark]
        : [tr('gate.v1.bank.hasNone'), C.bad]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color, fontWeight: 700 }}>
      <Icon name="passbook" size={16} />
      {text}
    </span>
  )
}

/**
 * Home → खाते की जानकारी (V1): pick a farmer of the village and add his bank account, outside a
 * purchase — passbook number verified through OnGrid. Needs the internet; the account then waits for the
 * office's approval. The roster is fetched again afterwards so the gate sees it at once.
 */
export function V1BankPage() {
  const navigate = useNavigate()
  const toast = useToast()
  const g = useGate()
  const sync = useSyncStatus()
  const queued = useQueuedBanks()
  const [q, setQ] = useState('')
  const [all, setAll] = useState(false)
  const [picked, setPicked] = useState<RosterFarmer | null>(null)
  /** Accounts added here this visit (user id → last 4), until the roster comes back. */
  const [added, setAdded] = useState<Map<string, string>>(new Map())
  useEffect(() => {
    void scrubSyncedBank()
  }, [sync.pending])
  const roster = g.roster?.farmers
  const farmers = useMemo(() => roster ?? [], [roster])
  const matched = useMemo(
    () =>
      farmers.filter((f) => farmerMatches(f, q)).sort((a, b) => a.name.localeCompare(b.name, 'hi')),
    [farmers, q],
  )
  if (!g.ready || !g.roster) return <V1Screen />
  const village = g.app?.village_name ?? g.info.registration?.team.name ?? ''
  const home = () => navigate('/m/gate')

  if (picked) {
    const b = bankOf(picked, added, queued)
    return (
      <BankCollectScreen
        key={picked.user_id}
        farmer={{ id: picked.user_id, name: picked.name }}
        current={
          b.verified
            ? { state: 'checked', last4: b.last4! }
            : b.pendingLast4
              ? { state: 'pending', last4: b.pendingLast4 }
              : b.queuedLast4
                ? { state: 'queued', last4: b.queuedLast4 }
                : null
        }
        header={
          <>
            <V1Header
              onBack={() => setPicked(null)}
              pending={sync.pending}
              icon="passbook"
              title={tr('gate.v1.bank.title')}
            />
            <div style={{ padding: '0 16px 4px' }}>
              <FarmerRow f={picked} village={village} note={<BankNote b={b} />} />
            </div>
          </>
        }
        onDone={(a) => {
          // A queued one shows from the outbox (useQueuedBanks) until the server answers.
          if (!a.queued) setAdded((m) => new Map(m).set(picked.user_id, a.last4))
          setPicked(null)
          toast.success(
            tr(a.queued ? 'gate.v1.bank.queuedAccount' : 'gate.v1.bank.saved', { last4: a.last4 }),
          )
          // Fresh roster so the gate shows the account; a failure only waits for the next update.
          void downloadCaches().catch(() => undefined)
        }}
        onSkip={() => setPicked(null)}
        skipLabel={tr(b.verified ? 'gate.v1.back' : 'gate.v1.bank.later')}
      />
    )
  }

  const shown = q.trim() || all ? matched : matched.slice(0, QUICK)
  const hidden = matched.length - shown.length
  return (
    <V1Screen
      main={{ gap: 8 }}
      header={
        <V1Header
          onBack={home}
          pending={sync.pending}
          icon="passbook"
          title={tr('gate.v1.bank.pick')}
        />
      }
      footer={
        <Footer>
          <WaitCta>{tr('gate.v1.find.tapName')}</WaitCta>
        </Footer>
      }
    >
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 56,
          padding: '0 12px',
          borderRadius: 12,
          border: `2px solid ${C.acc}`,
          background: C.surface,
          flexShrink: 0,
        }}
      >
        <Icon name="search" size={28} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={tr('gate.v1.find.name')}
          aria-label={tr('gate.v1.find.name')}
          enterKeyHint="search"
          autoComplete="off"
          style={{
            flexGrow: 1,
            minWidth: 0,
            border: 0,
            outline: 'none',
            background: 'transparent',
            fontFamily: "'Mukta', sans-serif",
            fontSize: 22,
            fontWeight: 600,
            color: C.ink,
          }}
        />
        {q && (
          <button
            type="button"
            onClick={() => setQ('')}
            aria-label={tr('gate.v1.find.clear')}
            style={{ border: 0, background: 'transparent', color: C.muted, padding: 6 }}
          >
            <Icon name="x" size={22} />
          </button>
        )}
      </label>
      <div style={{ fontSize: 15, color: C.muted, fontWeight: 600 }}>
        {q.trim()
          ? tr('gate.v1.find.matches', { n: matched.length })
          : tr('gate.v1.find.inVillage', { n: farmers.length, village })}
      </div>
      {matched.length === 0 ? (
        <div style={{ fontSize: 17, color: C.bad, fontWeight: 700 }}>{tr('gate.v1.find.none')}</div>
      ) : (
        shown.map((f) => (
          <FarmerRow
            key={f.user_id}
            f={f}
            village={village}
            note={<BankNote b={bankOf(f, added, queued)} />}
            onClick={() => setPicked(f)}
          />
        ))
      )}
      {hidden > 0 && (
        <Btn icon="down" onClick={() => setAll(true)}>
          {tr('gate.v1.find.showAll', { n: hidden })}
        </Btn>
      )}
    </V1Screen>
  )
}
